/**
 * =============================================================================
 * METRIC STRIP COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 *
 * Horizontal strip of MetricCards — KPIs section at the top of a page.
 * Renders any array of MetricCardInstance objects.
 * Pass context for dynamic token resolution (e.g. vendorId).
 */

import { Box, SimpleGrid, Stack, Text } from '@mantine/core';
import { memo } from 'react';
import type { MetricCardInstance } from '@diagon-alley/common';
import { MetricCard } from '../MetricCard';

const spacing = {
    xs: 8,
    sm: 16,
} as const;

export interface MetricStripProps {
    /** One-liner tagline rendered above the strip */
    tagline?: string;
    /** The metric card instance configs to render */
    instances: MetricCardInstance[];
    /**
     * Context tokens resolved into each instance's apiCalls at runtime.
     * Example: { vendorId: 'abc123' } resolves {{vendorId}} in endpoints and params.
     */
    context?: Record<string, string>;
    className?: string;
}

const MetricStrip: React.FC<MetricStripProps> = memo(
    ({ tagline, instances, context, className = '' }) => {
        if (!instances || instances.length === 0) return null;

        return (
            <Stack className={className} style={{ gap: spacing.sm }}>
                {tagline && (
                    <Text
                        className="text-slate-900"
                        style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}
                    >
                        {tagline}
                    </Text>
                )}

                <Box className="w-full">
                    <SimpleGrid
                        cols={{ base: 2, sm: 2, md: 4, lg: instances.length }}
                        spacing={spacing.sm}
                    >
                        {instances.map((instance) => (
                            <MetricCard
                                key={instance.id}
                                instance={instance}
                                context={context}
                            />
                        ))}
                    </SimpleGrid>
                </Box>
            </Stack>
        );
    },
);

MetricStrip.displayName = 'MetricStrip';

export default MetricStrip;
